import * as React from "react";
import moment from "moment";
import { FormikHelpers } from "formik";
import classNames from "classnames";
import DatePicker from "react-datepicker";
import { useTranslation } from "hooks";
import { CustomInput } from "./components";
import { TAB, ENTER, SPACE } from "./duck/constants";
import { Icon, Button, Typography } from "../..";
import { formHooks } from "../../form/duck";

import styles from "./DateInput.module.css";
import "react-datepicker/dist/react-datepicker.css";

import "moment/locale/es";

const language = localStorage.getItem("language") || "en";
moment.locale(language === "es" ? "es" : "en");

interface DateInputProps {
  required?: boolean;
  name: string;
  label: string;
  labelHidden?: boolean;
  dateFormat?: string;
  placeholderText?: string;
  className?: string;
  popperClassName?: string;
  setTouched?: FormikHelpers<any>["setFieldTouched"];
  onDateChange?: (value: Date | null) => void;
  minDate?: Date | null;
  value?: Date | null;
  disabled?: boolean;
}

const DateInput: React.FC<DateInputProps> = ({
  required,
  name,
  label,
  labelHidden = false,
  className,
  popperClassName,
  setTouched = () => {},
  dateFormat = "MMMM d, yyyy",
  placeholderText = "Date",
  onDateChange,
  minDate,
  value,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const inputRef = React.useRef(undefined);
  const dateRef: React.RefObject<any> = React.useRef(undefined);
  const [{ value: formValue, onChange, onBlur }] = formHooks.useField<string>({
    name,
  });

  const handleKeyDown = (event: React.KeyboardEvent): void => {
    if (event.keyCode === ENTER || event.keyCode === SPACE) {
      event.preventDefault();
      dateRef.current.setOpen(true);
    } else if (event.keyCode === TAB && event.shiftKey) {
      dateRef.current.setOpen(false);
    }
  };

  return (
    <div className={classNames(styles.wrapper, className)}>
      <DatePicker
        disabled={disabled}
        required={required}
        preventOpenOnFocus
        ref={dateRef}
        selected={value || (formValue && new Date(formValue)) || null}
        placeholderText={placeholderText}
        popperClassName={classNames(styles.popper, popperClassName)}
        calendarClassName={styles.calendar}
        formatWeekDay={(day) => day.substring(0, 1)}
        dateFormat={dateFormat}
        minDate={minDate as Date}
        customInput={
          <CustomInput
            label={label}
            labelHidden={labelHidden}
            field={name}
            ref={inputRef}
            suffix={<Icon name="calendar-outlined" />}
            dateFormat={dateFormat}
          />
        }
        onBlur={onBlur}
        onChange={(date: any) => {
          if (onDateChange) {
            onDateChange(date);
            // React Date Picker don't call onBlur callback after onChange callback
            setTouched(name, true);

            return;
          }

          if (onChange) {
            onChange({ value: date });
          }
          setTouched(name, true);
        }}
        onKeyDown={handleKeyDown}
        renderCustomHeader={({ date, decreaseMonth, increaseMonth }) => (
          <div className={styles.customHeader}>
            <Button
              iconOnly
              size="small"
              variant="clear"
              shape="rounded"
              title={t("assignments.prevMonth")}
              className={styles.headerControls}
              prefix={<Icon name="left-outlined" size="small" />}
              onClick={decreaseMonth}
            />
            <Typography component="span" variant="c1">
              {moment(date).format("MMMM")}
            </Typography>
            <Button
              iconOnly
              size="small"
              variant="clear"
              shape="rounded"
              title={t("assignments.nextMonth")}
              className={styles.headerControls}
              prefix={<Icon name="right-outlined" size="small" />}
              onClick={increaseMonth}
            />
          </div>
        )}
      />
    </div>
  );
};

export default DateInput;
